/**
 * PricingTierBandEditor — the volume-band table shared by both tier editors: the default
 * table on Pricing Management and a single client's own table on Client Edit.
 *
 * Billing is graduated, so the preview at the bottom is the honest way to read a table:
 * each band's lookups charge at that band's rate, which is not the same as "pick a rate by
 * total volume" and is easy to misjudge by eye.
 *
 * Two things this deliberately surfaces rather than hides:
 *   - a rate with more than 2 decimals, because ApiRequestLogs.Cost is numeric(10,2) and the
 *     per-lookup charge is rounded to cents
 *   - gaps and overlaps, because at billing time a lookup in no band is charged at the HIGHEST
 *     band instead of failing, which overcharges quietly
 */
import React, { useMemo } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  BandRow, validateBandRows, graduatedTotal, rateRoundsWhenBilled,
} from '../../services/pricingTiersService';

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PREVIEW_VOLUMES = [1000, 7000, 10000, 50000];

interface Props {
  rows: BandRow[];
  onChange: (rows: BandRow[]) => void;
  disabled?: boolean;
}

const PricingTierBandEditor: React.FC<Props> = ({ rows, onChange, disabled = false }) => {
  const validationError = useMemo(() => validateBandRows(rows), [rows]);

  const roundingRows = useMemo(
    () => rows.filter((r) => r.isActive && rateRoundsWhenBilled(r.ratePerUnit)),
    [rows],
  );

  const setRow = (index: number, patch: Partial<BandRow>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRow = () => {
    const last = rows[rows.length - 1];
    // A new band starts where the previous one ended, which is the only valid place for it.
    const nextMin = last && last.maxQuantity.trim() !== ''
      ? String(parseInt(last.maxQuantity, 10) + 1)
      : rows.length === 0 ? '1' : '';
    onChange([...rows, { minQuantity: nextMin, maxQuantity: '', ratePerUnit: '', isActive: true, description: '' }]);
  };

  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  return (
    <div>
      {rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-[#64748b] border border-dashed border-black/[0.12] rounded-lg">
          No volume bands. This service bills at its flat price.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
              <tr>
                <th className="text-left py-2">From</th>
                <th className="text-left">To</th>
                <th className="text-right">Rate / lookup</th>
                <th className="text-right">Bills at</th>
                <th className="text-left pl-4">Label</th>
                <th className="text-center">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rounds = r.isActive && rateRoundsWhenBilled(r.ratePerUnit);
                const billed = Number.isFinite(parseFloat(r.ratePerUnit))
                  ? Math.round(parseFloat(r.ratePerUnit) * 100) / 100
                  : null;
                return (
                  <tr key={i} className="border-b border-black/[0.04]">
                    <td className="py-2">
                      <input
                        type="text" inputMode="numeric" value={r.minQuantity} disabled={disabled}
                        onChange={(e) => setRow(i, { minQuantity: e.target.value })}
                        className="w-24 px-2 py-1 rounded border border-black/[0.1] text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-black/[0.03]"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="text" inputMode="numeric" value={r.maxQuantity} disabled={disabled}
                        onChange={(e) => setRow(i, { maxQuantity: e.target.value })}
                        placeholder="and up"
                        className="w-24 px-2 py-1 rounded border border-black/[0.1] text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-black/[0.03]"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <div className="inline-flex items-center">
                        <span className="text-[#94a3b8] mr-0.5">$</span>
                        <input
                          type="text" inputMode="decimal" value={r.ratePerUnit} disabled={disabled}
                          onChange={(e) => setRow(i, { ratePerUnit: e.target.value })}
                          className={`w-24 px-2 py-1 rounded border text-right text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-black/[0.03] ${rounds ? 'border-amber-400' : 'border-black/[0.1]'}`}
                        />
                      </div>
                    </td>
                    <td className={`py-2 text-right whitespace-nowrap font-semibold ${rounds ? 'text-amber-700' : 'text-[#0f172a]'}`}>
                      {billed == null ? '—' : money(billed)}
                    </td>
                    <td className="py-2 pl-4">
                      <input
                        type="text" value={r.description} disabled={disabled}
                        onChange={(e) => setRow(i, { description: e.target.value })}
                        placeholder="optional"
                        className="w-40 px-2 py-1 rounded border border-black/[0.1] text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-black/[0.03]"
                      />
                    </td>
                    <td className="py-2 text-center">
                      <input
                        type="checkbox" checked={r.isActive} disabled={disabled}
                        onChange={(e) => setRow(i, { isActive: e.target.checked })}
                      />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button" onClick={() => removeRow(i)} disabled={disabled}
                        className="text-[#94a3b8] hover:text-[#c41e3a] disabled:opacity-30"
                        aria-label="Remove band"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button" onClick={addRow} disabled={disabled}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#c41e3a] hover:text-[#a51830] disabled:opacity-40"
      >
        <Plus className="w-3.5 h-3.5" /> Add band
      </button>

      {validationError && (
        <div className="mt-4 p-3 rounded-lg border bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{validationError}</span>
        </div>
      )}

      {roundingRows.length > 0 && (
        <div className="mt-3 p-3 rounded-lg border bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {roundingRows.length === 1 ? 'One band has' : `${roundingRows.length} bands have`} a rate with more
            than 2 decimals. Each lookup is billed to the nearest cent, so the "bills at" column is what the
            client is actually charged — up to half a cent per lookup away from the rate entered.
          </span>
        </div>
      )}

      {!validationError && rows.some((r) => r.isActive) && (
        <div className="mt-4 rounded-lg bg-black/[0.02] border border-black/[0.06] px-4 py-3">
          <p className="text-xs uppercase tracking-wider text-[#94a3b8] mb-2">Monthly total at this table</p>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {PREVIEW_VOLUMES.map((v) => (
              <div key={v}>
                <span className="text-xs text-[#64748b]">{v.toLocaleString()} lookups</span>
                <span className="block text-sm font-semibold text-[#0f172a]">
                  {money(graduatedTotal(rows, v))}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#94a3b8] mt-2">
            Graduated: each band's lookups bill at that band's own rate, not one rate for the whole month.
          </p>
        </div>
      )}
    </div>
  );
};

export default PricingTierBandEditor;
